/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useMemo } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	getRelativeHeadingIndentation,
	type RelativeHeadingIndentation,
} from './getRelativeHeadingIndentation.ts';
import type { TableOfContentsItem, TableOfContentsProps } from './tableOfContentsTypes.ts';

const styles = cssMap({
	container: {
		width: '248px',
		maxWidth: '248px',
		// Default cap; consumers can override at runtime via the `maxHeight` prop.
		maxHeight: '480px',
		overflowY: 'auto',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		paddingTop: token('space.200'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
	},
	list: {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		listStyle: 'none',
		listStyleType: 'none',
		display: 'flex',
		flexDirection: 'column',
		rowGap: token('space.050'),
	},
	row: {
		minWidth: '0',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		listStyle: 'none',
		listStyleType: 'none',
	},
	item: {
		boxSizing: 'border-box',
		width: '100%',
		minWidth: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'start',
		height: '1.5rem',
		textAlign: 'start',
		borderRadius: token('radius.small'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			textDecoration: 'none',
		},
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:focus': {
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
	},
	text: {
		width: '100%',
		minWidth: '0',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
		'&:visited': {
			color: token('color.text.selected'),
		},
		'&:focus': {
			color: token('color.text.selected'),
		},
	},
});

const indentationStyles = cssMap({
	0: {
		marginLeft: token('space.0'),
	},
	1: {
		marginLeft: token('space.150'),
	},
	2: {
		marginLeft: token('space.300'),
	},
	3: {
		marginLeft: token('space.300'),
		paddingLeft: token('space.150'),
	},
	4: {
		marginLeft: token('space.600'),
	},
	5: {
		marginLeft: token('space.600'),
		paddingLeft: token('space.150'),
	},
});

type TableOfContentsRowProps = {
	indentation: RelativeHeadingIndentation;
	isSelected: boolean;
	item: TableOfContentsItem;
	onSelect: (item: TableOfContentsItem) => void;
	renderEmoji?: TableOfContentsProps['renderEmoji'];
};

function TableOfContentsRowContent({
	item,
	renderEmoji,
}: {
	item: TableOfContentsItem;
	renderEmoji?: TableOfContentsProps['renderEmoji'];
}): ReactNode {
	if (!item.content) {
		return item.text;
	}

	return item.content.map((segment, index) => {
		if (segment.type === 'emoji') {
			return <Fragment key={index}>{renderEmoji?.(segment)}</Fragment>;
		}
		return segment.text;
	});
}

function TableOfContentsRow({
	indentation,
	item,
	isSelected,
	onSelect,
	renderEmoji,
}: TableOfContentsRowProps) {
	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>) => {
			// Preserve native anchor behaviour for modifier/middle clicks so users
			// can open the heading link in a new tab, copy it, etc.
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
				return;
			}
			event.preventDefault();
			onSelect(item);
		},
		[onSelect, item],
	);

	// Links to the same heading anchor as the "copy heading link" button.
	const href = `#${encodeURIComponent(item.headingId)}`;

	return (
		<li css={[styles.row, indentationStyles[indentation]]}>
			<Anchor
				aria-current={isSelected ? 'location' : undefined}
				href={href}
				onClick={handleClick}
				xcss={cx(styles.item, isSelected && styles.selected)}
			>
				<span css={styles.text} title={item.text}>
					<TableOfContentsRowContent item={item} renderEmoji={renderEmoji} />
				</span>
			</Anchor>
		</li>
	);
}

export function TableOfContents({
	items,
	onSelect,
	selectedHeadingId,
	label,
	emptyState,
	maxHeight,
	renderEmoji,
}: TableOfContentsProps): ReactNode {
	const indentationByItem = useMemo(() => getRelativeHeadingIndentation(items), [items]);
	const selectedItemIndex = useMemo(
		() => items.findIndex((item) => item.headingId === selectedHeadingId),
		[items, selectedHeadingId],
	);

	if (items.length === 0) {
		return (
			<div css={styles.container} style={{ maxHeight }}>
				{emptyState ?? null}
			</div>
		);
	}

	return (
		<div css={styles.container} style={{ maxHeight }}>
			<nav aria-label={label}>
				<ul css={styles.list}>
					{items.map((item, index) => (
						<TableOfContentsRow
							indentation={indentationByItem[index]}
							key={item.headingId}
							item={item}
							isSelected={index === selectedItemIndex}
							onSelect={onSelect}
							renderEmoji={renderEmoji}
						/>
					))}
				</ul>
			</nav>
		</div>
	);
}
